/**
 * Katalog vs annons — the one rule for where a listing is shown (P25009-410),
 * shared by the browser (search, Dina annonser, the profile, the listing page,
 * Anslut ditt lager) and the server (the advertise endpoint, the feeds and
 * sitemaps, the alerts, Bygg paket).
 *
 * An article in the catalogue and an ad on the marketplace are the SAME
 * listing; `publicData.visibility` records which one it is. Uploading fills
 * the catalogue (free, shown on the seller's profile); advertising is a
 * separate, deliberate choice that takes effect at once.
 *
 * The mechanism is a search filter. `visibility` has a CLI search schema
 * (enum, default `marketplace`) on each marketplace — see
 * marketplaces/<id>/search-schema.json — so `pub_visibility=marketplace`
 * really filters, and every listing WITHOUT the key (all ads made before this,
 * every wizard listing) counts as marketplace with no backfill. Every
 * buyer-facing listing query adds MARKETPLACE_FILTER; queries that are the
 * seller's own (Dina annonser, own stock) or by author for the profile's
 * Katalog do not.
 *
 * Without the schema the filter is silently ignored and catalogue rows show
 * everywhere, so the schema must exist on a marketplace BEFORE this code runs
 * against it.
 *
 * CommonJS, so the server can require it.
 */

const VISIBILITY_CATALOG = 'catalog';
const VISIBILITY_MARKETPLACE = 'marketplace';

/* What every buyer-facing listing query adds. */
const MARKETPLACE_FILTER = { pub_visibility: VISIBILITY_MARKETPLACE };
/* The profile's Katalog and the seller's Katalog tab. */
const CATALOGUE_FILTER = { pub_visibility: VISIBILITY_CATALOG };

/* Where a listing stands, from the seller's point of view. */
const EXPOSURE = {
  // In the catalogue: on the seller's profile, never in marketplace search.
  CATALOGUE: 'catalogue',
  // An ad still waiting for the marketplace's approval: imports from before
  // P25009-410 and the supplier connectors still create these.
  REQUESTED: 'requested',
  // A live ad.
  AD: 'ad',
  CLOSED: 'closed',
  DRAFT: 'draft',
  // An import the seller undid while it awaited approval. Must never be published.
  ROLLED_BACK: 'rolled_back',
  DELETED: 'deleted',
};

/** For an in-memory check of a listing the API already returned. */
const isMarketplaceVisible = publicData => publicData?.visibility !== VISIBILITY_CATALOG;

/**
 * @param {Object} attributes a listing's attributes ({state, publicData, metadata})
 * @returns {string} one of EXPOSURE
 */
const exposureOf = (attributes = {}) => {
  const { state, publicData = {}, metadata = {} } = attributes || {};
  if (publicData?.isDeleted === true) return EXPOSURE.DELETED;
  if (state === 'draft') return EXPOSURE.DRAFT;
  if (state === 'closed') return EXPOSURE.CLOSED;
  // `metadata` is only readable by the server; the browser never sees it.
  if (state === 'pendingApproval' && metadata?.importRolledBack === true) {
    return EXPOSURE.ROLLED_BACK;
  }
  /* Normally published. A pendingApproval catalogue row is one imported
   * before catalogue rows were published, waiting for the one-off
   * publishCatalogueRows script. */
  if (publicData?.visibility === VISIBILITY_CATALOG) return EXPOSURE.CATALOGUE;
  if (state === 'published') return EXPOSURE.AD;
  if (state === 'pendingApproval') return EXPOSURE.REQUESTED;
  return state || '';
};

const isCatalogueRow = attributes => exposureOf(attributes) === EXPOSURE.CATALOGUE;

/* The words (BR-4, AC-4). "Katalog" is free and profile-only; an "annons" is
 * on the marketplace. A catalogue row is never called an annons. */
const EXPOSURE_LABELS = {
  [EXPOSURE.CATALOGUE]: 'Katalog',
  [EXPOSURE.REQUESTED]: 'Väntar på godkännande',
  [EXPOSURE.AD]: 'Annons',
  [EXPOSURE.CLOSED]: 'Stängd',
  [EXPOSURE.DRAFT]: 'Utkast',
  [EXPOSURE.ROLLED_BACK]: 'Återställd',
  [EXPOSURE.DELETED]: 'Borttagen',
};

module.exports = {
  VISIBILITY_CATALOG,
  VISIBILITY_MARKETPLACE,
  MARKETPLACE_FILTER,
  CATALOGUE_FILTER,
  EXPOSURE,
  EXPOSURE_LABELS,
  exposureOf,
  isCatalogueRow,
  isMarketplaceVisible,
};
